// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseAvatarGroup,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  Loading,
} from '~/components/common';
import { FormScheduleModal } from '~/components/module/schedules';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import { IPaginationResponse } from '~/utils/interface/base';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { formatDate, formatTime } from '~/utils/helper/common';
import { DateFormatEnum, RoleEnum, ScheduleTypeEnum, TimeFormatEnum } from '~/utils/enum/common';
import { IQueryParamSchedulesDashboard, IScheduleDashboard } from '~/utils/interface/schedule';
import { IHeaderCellType } from '~/utils/interface/common';
import { getScheduleDashboardList } from '~/thunks/schedule/scheduleThunk';
import { scheduleActions, selectIsRefreshScheduleList } from '~/thunks/schedule/scheduleSlice';
// Styles, images, icons
import styles from './ScheduleToday.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columnsSchedule = (
  t: TFunction,
  onView: (id: string) => void
): IHeaderCellType<IScheduleDashboard>[] => {
  return [
    {
      id: 'eventName',
      label: t('dashboard_schedule_title_name', { ns: 'admin/dashboard' }),
      render: (record: IScheduleDashboard) => record?.eventName || EMPTY_STRING,
    },
    {
      id: 'type',
      label: t('dashboard_schedule_title_type', { ns: 'admin/dashboard' }),
      render: (record: IScheduleDashboard) => {
        switch (record.businessType) {
          case ScheduleTypeEnum.BOOKING_A_JOB:
            return t('dashboard_schedule_title_type_job', { ns: 'admin/dashboard' });
          // case ScheduleTypeEnum.BOOKING_A_CALL:
          //   return t('dashboard_schedule_title_type_contact', { ns: 'admin/dashboard' });
          // case ScheduleTypeEnum.BOOKING_A_RETURN_CUSTOMER:
          //   return t('dashboard_schedule_title_type_customer', { ns: 'admin/dashboard' });
          case ScheduleTypeEnum.EVENT:
            return t('dashboard_schedule_title_type_event', { ns: 'admin/dashboard' });
          case ScheduleTypeEnum.ESTIMATE:
            return t('dashboard_schedule_title_type_estimate', { ns: 'admin/dashboard' });
        }
      },
    },
    {
      id: 'startTime',
      label: t('dashboard_schedule_title_start_time', { ns: 'admin/dashboard' }),
      render: (record: IScheduleDashboard) => {
        return record?.startTime
          ? formatTime(record?.startTime, TimeFormatEnum.HOUR_MINUTE_AM_PM)
          : EMPTY_STRING;
      },
      minWidth: 80,
    },
    {
      id: 'endTime',
      label: t('dashboard_schedule_title_end_time', { ns: 'admin/dashboard' }),
      render: (record: IScheduleDashboard) => {
        return record?.endTime ? formatTime(record?.endTime, TimeFormatEnum.HOUR_MINUTE_AM_PM) : EMPTY_STRING;
      },
      minWidth: 80,
    },
    {
      id: 'assignees',
      label: t('dashboard_schedule_title_assignees', { ns: 'admin/dashboard' }),
      render: (record: IScheduleDashboard) => (
        <BaseAvatarGroup avatars={record?.assignees || []} borderColor='#FFF' max={5} />
      ),
    },
    {
      id: 'action',
      label: t('dashboard_schedule_title_action', { ns: 'admin/dashboard' }),
      align: 'left',
      render: (record: IScheduleDashboard) => {
        return <BaseTableOption onView={() => onView(record.id)} />;
      },
    },
  ];
};

const ScheduleToday = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/task', 'admin/dashboard']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshScheduleList = useSelector(selectIsRefreshScheduleList);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentRole] = useState<string | null>(localStorage.getItem(StorageEnum.ROLE));

  const [scheduleList, setScheduleList] = useState<IScheduleDashboard[]>([]);
  const [paramObject, setParamObject] = useState<IQueryParamSchedulesDashboard>({
    page: pageSelected,
    limit: limitPage,
    startDate: formatDate(new Date(), DateFormatEnum.YYYY_MM_DD),
    endDate: formatDate(new Date(), DateFormatEnum.YYYY_MM_DD),
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [scheduleSelected, setScheduleSelected] = useState<string>(EMPTY_STRING);
  const [isShowFormScheduleModal, setIsShowFormScheduleModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetScheduleList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshScheduleList) return;

    handleGetScheduleList(paramObject);
    dispatch(scheduleActions.setRefreshScheduleList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshScheduleList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetScheduleList = (params: IQueryParamSchedulesDashboard) => {
    setIsLoading(true);

    dispatch(getScheduleDashboardList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setScheduleList(responses);
        setPagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleViewSchedule = (scheduleId: string) => {
    const itemSchedule = scheduleList.find((item) => item.id === scheduleId);

    if (itemSchedule?.businessType === ScheduleTypeEnum.BOOKING_A_JOB && currentRole === RoleEnum.ADMIN) {
      navigate(`${adminRouteAbsolute.jobs}/${itemSchedule.businessId}`);
      return;
    }

    if (itemSchedule?.businessType === ScheduleTypeEnum.BOOKING_A_JOB && currentRole === RoleEnum.STAFF) {
      navigate(`${staffRouteAbsolute.jobs}/${itemSchedule.businessId}`);
      return;
    }

    if (itemSchedule?.businessType === ScheduleTypeEnum.BOOKING_A_CALL && currentRole === RoleEnum.ADMIN) {
      navigate(`${adminRouteAbsolute.contacts}/${itemSchedule.businessId}`);
      return;
    }

    if (itemSchedule?.businessType === ScheduleTypeEnum.BOOKING_A_CALL && currentRole === RoleEnum.STAFF) {
      navigate(`${staffRouteAbsolute.contacts}/${itemSchedule.businessId}`);
      return;
    }

    if (
      itemSchedule?.businessType === ScheduleTypeEnum.BOOKING_A_RETURN_CUSTOMER &&
      currentRole === RoleEnum.ADMIN
    ) {
      navigate(`${adminRouteAbsolute.customers}/${itemSchedule.businessId}`);
      return;
    }

    if (
      itemSchedule?.businessType === ScheduleTypeEnum.BOOKING_A_RETURN_CUSTOMER &&
      currentRole === RoleEnum.STAFF
    ) {
      navigate(`${staffRouteAbsolute.customers}/${itemSchedule.businessId}`);
      return;
    }

    if (itemSchedule?.businessType === ScheduleTypeEnum.ESTIMATE && currentRole === RoleEnum.ADMIN) {
      navigate(`${adminRouteAbsolute.estimate}/${itemSchedule.businessId}`);
      return;
    }

    if (itemSchedule?.businessType === ScheduleTypeEnum.ESTIMATE && currentRole === RoleEnum.STAFF) {
      navigate(`${staffRouteAbsolute.estimate}/${itemSchedule.businessId}`);
      return;
    }

    if (itemSchedule?.businessType === ScheduleTypeEnum.EVENT) {
      setScheduleSelected(scheduleId);
      setIsShowFormScheduleModal(true);
      return;
    }
  };

  const handleCloseFormScheduleModal = () => {
    setIsShowFormScheduleModal(false);
    setScheduleSelected(EMPTY_STRING);
  };

  const handleViewSchedulePage = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.schedules);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.schedules);
        break;
    }
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: rowPerPage,
    });
    // setSearchParamsSchedule({
    //   ...paramsSchedule,
    //   page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    //   limit: String(rowPerPage),
    // });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    // setSearchParamsSchedule({ ...paramsSchedule, page: String(page), limit: String(limitPageSchedule) });
  };

  //#endregion Handle Function

  return (
    <Box id='scheduleTodayComponent' className={cx('containerSchedule')}>
      <BaseToolbar title={t('dashboard_schedule_title', { ns: 'admin/dashboard' })}>
        <Button variant='text' onClick={handleViewSchedulePage} className={cx('btnViewSchedule')}>
          <Typography>{t('dashboard_schedule_view', { ns: 'admin/dashboard' })}</Typography>
        </Button>
      </BaseToolbar>

      <Box component={'div'} className={cx('bodySchedule')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ONE}
          columns={columnsSchedule(t, handleViewSchedule)}
          dataSource={scheduleList || []}
          onPageChange={handlePageChange}
          onRowClick={handleViewSchedule}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowFormScheduleModal && (
        <FormScheduleModal onClose={handleCloseFormScheduleModal} scheduleId={scheduleSelected} />
      )}
    </Box>
  );
};

export default ScheduleToday;
